// Diario de actividad del TPV — lógica pura (sin DOM, sin Redux, sin red).
//
// Cada acción del camarero en el TPV (entrar en una mesa, añadir/quitar un
// producto del carrito aunque nunca se guarde, comandar, cobrar, cajón…)
// se convierte en un evento pequeño que se envía por lotes a
// `POST tpv-journal/batch`. El servidor pone empleado/dispositivo/restaurante
// y une cada evento con lo que hizo el backend mediante el `actionId`
// (el mismo de `X-Action-Id`, ver utils/debug/debugTrace).

import { screenForPath } from "src/utils/debug/debugScreen";

export const JOURNAL_TYPES = new Set([
  "mesa.enter",
  "mesa.leave",
  "session.open",
  "cart.add",
  "cart.qty",
  "cart.remove",
  "cart.note",
  "cart.clear",
  "comandar.click",
  // 2026-10-26 · Guardado automático al salir de la mesa (sin papel) e
  // «Imprimir precuenta / ticket»: así sus impresiones cuelgan de la acción.
  "comandar.autosave",
  "print.click",
  "pay.open",
  "pay.method",
  "pay.confirm",
  "pay.cancel",
  "drawer.click",
  "discount.apply",
  "mesa.move",
  "comensal.assign",
  "lease.forced",
  // Aviso de tiempo real (pastilla de estado del TPV): caído / recuperado.
  "realtime.lost",
  "realtime.restored",
  "ui.error",
  "ui.click",
  "ui.other",
]);

export const MAX_DATA_CHARS = 800;
export const MAX_BATCH = 500;
export const FLUSH_INTERVAL_MS = 5000;
export const FLUSH_AT = 100;
export const MAX_QUEUE = 5000;
export const KEEPALIVE_MAX_BYTES = 60000;

export function newEventId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** El diario solo graba en pantallas de TPV del personal (nunca carta QR ni públicas). */
export function isJournalPath(pathname) {
  return screenForPath(pathname).startsWith("tpv.");
}

const strCut = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * Limita `data` a MAX_DATA_CHARS serializado: primero recorta strings
 * largos y arrays, y si aun así no cabe, se queda con las claves
 * escalares más importantes (en orden de inserción).
 */
export function truncateData(data, max = MAX_DATA_CHARS) {
  if (data == null || typeof data !== "object") return data == null ? undefined : { value: data };
  const fits = (o) => JSON.stringify(o).length <= max;
  if (fits(data)) return data;
  const shallow = {};
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === "string") shallow[k] = strCut(v, 120);
    else if (Array.isArray(v)) shallow[k] = v.slice(0, 5).map((x) => (typeof x === "string" ? strCut(x, 40) : x));
    else if (v && typeof v === "object") shallow[k] = strCut(JSON.stringify(v), 120);
    else shallow[k] = v;
  }
  if (fits(shallow)) return shallow;
  const out = { truncated: true };
  for (const [k, v] of Object.entries(shallow)) {
    if (Array.isArray(v) || (v && typeof v === "object")) continue;
    out[k] = v;
    if (!fits(out)) {
      delete out[k];
      break;
    }
  }
  return out;
}

/** Construye un evento del diario. `ctx` = { now, actionId, screen, mesaId, sessionId }. */
export function buildEvent(type, data, extra = {}, ctx = {}) {
  const t = JOURNAL_TYPES.has(type) ? type : "ui.other";
  const ev = {
    clientEventId: newEventId(),
    ts: new Date(ctx.now != null ? ctx.now : Date.now()).toISOString(),
    type: t,
  };
  const mesaId = extra.mesaId !== undefined ? extra.mesaId : ctx.mesaId;
  const sessionId = extra.sessionId !== undefined ? extra.sessionId : ctx.sessionId;
  if (mesaId) ev.mesaId = String(mesaId);
  if (sessionId) ev.sessionId = String(sessionId);
  if (extra.pedidoId != null) ev.pedidoId = extra.pedidoId;
  const actionId = extra.actionId || ctx.actionId;
  if (actionId) ev.actionId = actionId;
  if (ctx.screen) ev.screen = ctx.screen;
  const payload = t === type ? data : { originalType: String(type).slice(0, 40), ...(data || {}) };
  const d = truncateData(payload);
  if (d && Object.keys(d).length) ev.data = d;
  return ev;
}

/** Backoff de reintento: 5 s, 10 s, 20 s… hasta 5 min. */
export function backoffMs(attempt) {
  const a = Math.max(0, attempt | 0);
  return Math.min(5000 * 2 ** a, 300000);
}

/** ¿Merece la pena reintentar el lote? (red, 5xx, 429, 401/403 → sí; resto de 4xx → no). */
export function shouldRetry(status) {
  if (status == null || status === 0) return true;
  if (status === 429 || status === 401 || status === 403 || status === 408) return true;
  return status >= 500;
}

/** Lote que cabe en un `fetch keepalive` (límite ~64 KB del navegador). */
export function takeKeepaliveBatch(queue, maxBytes = KEEPALIVE_MAX_BYTES) {
  const out = [];
  let bytes = 12;
  for (const ev of queue) {
    const n = JSON.stringify(ev).length + 1;
    if (bytes + n > maxBytes || out.length >= MAX_BATCH) break;
    out.push(ev);
    bytes += n;
  }
  return out;
}

// ── Diferencias del carrito ───────────────────────────────────────────

const complementIds = (line) =>
  (line?.complementos || [])
    .map((c) => `${c?.id ?? ""}`)
    .sort()
    .join(",");

/** Identidad de una línea del carrito (sin notas: un cambio de nota es `cart.note`). */
export function cartLineKey(line) {
  return [
    line?.id ?? "",
    line?.idComplemento ?? -1,
    line?.idComensal ?? 0,
    line?.old ? 1 : 0,
    complementIds(line),
  ].join("|");
}

function indexCart(cart) {
  const map = new Map();
  for (const line of cart || []) {
    if (!line) continue;
    const key = cartLineKey(line);
    const prev = map.get(key);
    const qty = Number(line.quantity) || 0;
    if (prev) prev.qty += qty;
    else map.set(key, { line, qty });
  }
  return map;
}

const lineName = (l) => l?.nombre ?? l?.name ?? null;
const complementNames = (l) =>
  (l?.complementos || []).map((c) => c?.nombre ?? c?.name ?? c?.id).filter((x) => x != null);

/**
 * Compara el carrito antes/después de una acción del camarero y devuelve
 * los eventos del diario: `cart.add`, `cart.qty`, `cart.remove`, `cart.note`.
 * `saved` = la línea ya estaba comandada/guardada (`old`); una línea que se
 * quita sin haberse guardado nunca llegó a la BD y solo queda aquí.
 */
export function diffCart(before, after) {
  if (before === after) return [];
  const a = indexCart(before);
  const b = indexCart(after);
  const events = [];
  for (const [key, cur] of b) {
    const prev = a.get(key);
    const l = cur.line;
    const base = { productId: l?.id ?? null, name: lineName(l) };
    if (!prev) {
      if (cur.qty <= 0) continue;
      const comps = complementNames(l);
      events.push({
        type: "cart.add",
        data: { ...base, qty: cur.qty, price: Number(l?.precio) || 0, ...(comps.length ? { complements: comps } : {}), saved: !!l?.old },
      });
    } else {
      if (prev.qty !== cur.qty) {
        events.push({ type: "cart.qty", data: { ...base, from: prev.qty, to: cur.qty, saved: !!l?.old } });
      }
      const n0 = (prev.line?.notas ?? "").trim();
      const n1 = (l?.notas ?? "").trim();
      if (n0 !== n1) events.push({ type: "cart.note", data: { ...base, note: n1 } });
    }
  }
  for (const [key, prev] of a) {
    if (b.has(key) || prev.qty <= 0) continue;
    const l = prev.line;
    events.push({
      type: "cart.remove",
      data: { productId: l?.id ?? null, name: lineName(l), qty: prev.qty, price: Number(l?.precio) || 0, saved: !!l?.old },
    });
  }
  return events;
}

export const cartTotals = (lines) => ({
  lines: (lines || []).length,
  total: Math.round((lines || []).reduce((s, p) => s + (Number(p?.precio) || 0) * (Number(p?.unidades ?? p?.quantity) || 0), 0) * 100) / 100,
});
